import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Loader,
  Center,
  TextInput,
  Button,
  Avatar,
  Box,
  Collapse,
  Divider,
  ActionIcon,
  UnstyledButton,
  Checkbox,
  Paper,
  Modal,
  Tooltip,
  Tabs,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useSearchParams } from 'react-router-dom';
import { Search, X, CheckSquare, ChevronRight, ChevronDown, Calendar, Link as LinkIcon } from 'react-feather';
import { copyTaskLink } from '../../utils/taskLinks';
import { tasksApi } from '../../services/api/tasks';
import { cohortsApi } from '../../services/api/cohorts';
import { useAuthStore } from '../../stores/auth';
import {
  Pagination,
  EmptyState,
  AvatarFilterBar,
  FilterPanel,
  SortPanel,
  GroupPanel,
} from '../../components/common';
import type { AvatarFilterItem, ActiveFilterRow, FilterFieldConfig } from '../../components/common';
import type { GroupLevel, GroupState } from '../../components/common/GroupPanel';
import type {
  CrossCohortTaskRow,
  CrossCohortTaskFilters,
  TaskAssignee,
  TaskStatus,
  TaskGroupByField,
  TaskGroupCount,
  TaskWithDetails,
  CohortWithDetails,
} from '../../types';
import { useSubtaskExpansion } from '../../hooks/useSubtaskExpansion';
import { formatDateOnly } from '../../utils/formatters';
import { isDateOnlyPast } from '../../utils/dateHelpers';
import { getAvatarColor, getInitials } from '../../utils/avatarHelpers';
import { TASK_STATUS_CONFIG, TASK_PHASE_OPTIONS } from '../../config/taskConfig';
import { TaskDetailDrawer } from '../cohorts/components/TaskDetailDrawer';
import type { SortState } from '../../utils/sortTasks';
import classes from './TasksPage.module.css';

// Status options for filter panel
const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'complete', label: 'Completed' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Sort field options
const SORT_FIELDS = [
  { value: 'due_date', label: 'Due Date' },
  { value: 'partner', label: 'Partner' },
  { value: 'cohort', label: 'Cohort' },
  { value: 'status', label: 'Status' },
];

// Group field options
const GROUP_FIELDS = [
  { value: 'cohort', label: 'Cohort' },
  { value: 'phase', label: 'Phase' },
  { value: 'status', label: 'Status' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'partner', label: 'Partner' },
];

// Quick filter configs for FilterPanel
// MED-753: removed 'My Tasks Only' chip — replaced by the My Tasks tab
// MED-837: added 'Incomplete' (default-active on My Tasks tab)
const QUICK_FILTERS = [
  { key: 'incomplete', label: 'Incomplete' },
  { key: 'due_this_week', label: 'Due This Week + Overdue' },
];

// MED-753: Tasks page tab keys
type TasksTab = 'all' | 'mine';

// MED-753: incomplete task statuses (excludes complete + cancelled)
const INCOMPLETE_TASK_STATUSES: TaskStatus[] = ['not_started', 'in_progress', 'blocked'];

type SortByField = 'due_date' | 'partner' | 'cohort' | 'status';
type SortDirection = 'asc' | 'desc';

// MED-837: per-tab filter/sort/group state (independent across tabs — see AC15/AC16)
interface TasksTabState {
  search: string;
  selectedAssigneeIds: string[];
  selectedCohortIds: string[];
  selectedPhases: string[];
  selectedStatuses: string[];
  sortBy: SortByField;
  sortOrder: SortDirection;
  quickFilters: string[];
  groupBy: GroupLevel | null;
  expandedGroups: Set<string>;
  addedFilterKeys: Set<string>;
  page: number;
  pageSize: number;
}

function createInitialTabState(tab: TasksTab): TasksTabState {
  return {
    search: '',
    selectedAssigneeIds: [],
    selectedCohortIds: [],
    selectedPhases: [],
    selectedStatuses: [],
    sortBy: 'due_date',
    sortOrder: 'asc',
    // AC18: My Tasks defaults to "Incomplete" quick filter active; user can deselect
    quickFilters: tab === 'mine' ? ['incomplete'] : [],
    groupBy: null,
    expandedGroups: new Set(),
    addedFilterKeys: new Set(),
    page: 1,
    pageSize: 50,
  };
}

/**
 * Hook that backs a piece of UI state with separate per-tab values.
 * Read returns the active tab's value; write updates only the active tab's slice.
 * Setter is stable across renders (reads activeTab via ref) so callbacks can keep [] deps.
 */
function useTabState<T>(
  activeTab: TasksTab,
  initial: Record<TasksTab, T>,
): [T, (updater: T | ((prev: T) => T)) => void] {
  const [byTab, setByTab] = useState<Record<TasksTab, T>>(initial);
  const tabRef = useRef(activeTab);
  tabRef.current = activeTab;
  const setValue = useCallback((updater: T | ((prev: T) => T)) => {
    setByTab((prev) => {
      const tab = tabRef.current;
      const next = typeof updater === 'function'
        ? (updater as (p: T) => T)(prev[tab])
        : updater;
      return { ...prev, [tab]: next };
    });
  }, []);
  return [byTab[activeTab], setValue];
}

// MED-753: column width constants — at narrow viewports we drop the assignee name (avatar only) so columns stay on one row
const TASK_COL_WIDTH = 380;
const ASSIGNEE_COL_WIDTH_DEFAULT = 120;
const ASSIGNEE_COL_WIDTH_COMPACT = 40; // avatar-only
const COMPACT_BREAKPOINT = '(max-width: 1370px)';

// Status display label mapping for group headers
const STATUS_GROUP_LABELS: Record<string, string> = {
  not_started: 'Pending',
  in_progress: 'In Progress',
  complete: 'Completed',
  blocked: 'Blocked',
  cancelled: 'Cancelled',
};

/**
 * Check if a task is overdue
 */
function isTaskOverdue(task: CrossCohortTaskRow): boolean {
  if (!task.due_date) return false;
  if (task.status === 'complete' || task.status === 'cancelled') return false;
  return isDateOnlyPast(task.due_date);
}

/**
 * Get week date range (Sunday to Saturday)
 */
function getThisWeekRange(): { from: string; to: string } {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - dayOfWeek);
  sunday.setHours(0, 0, 0, 0);
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  return {
    from: sunday.toISOString().split('T')[0],
    to: saturday.toISOString().split('T')[0],
  };
}

export function TasksPage() {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // MED-753: drop assignee name (avatar-only) on narrow screens to keep all columns on one row
  const isCompact = useMediaQuery(COMPACT_BREAKPOINT) ?? false;
  const assigneeColWidth = isCompact ? ASSIGNEE_COL_WIDTH_COMPACT : ASSIGNEE_COL_WIDTH_DEFAULT;

  // Task detail drawer state
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskCohortId, setSelectedTaskCohortId] = useState<string>('');
  const [selectedTaskCohortName, setSelectedTaskCohortName] = useState<string>('');

  // Data state — flat mode (no grouping)
  const [tasks, setTasks] = useState<CrossCohortTaskRow[]>([]);
  const [assignees, setAssignees] = useState<TaskAssignee[]>([]);
  const [cohorts, setCohorts] = useState<CohortWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalTasks, setTotalTasks] = useState(0);

  // MED-753: Active top-level tab ('all' = current behavior, 'mine' = flattened due-date list)
  const [activeTab, setActiveTab] = useState<TasksTab>('all');

  // MED-837: Filter / sort / group / pagination state is held per-tab so that
  // switching tabs preserves each tab's own state (AC15, AC16). My Tasks tab
  // initializes with the "Incomplete" quick filter active by default (AC18).
  const [search, setSearch] = useTabState<string>(activeTab, { all: '', mine: '' });
  // Debounce search by 300ms while the user types within a tab, but reflect the
  // new tab's value synchronously on tab switch so the fetch effect never issues
  // a stale request using the previous tab's debouncedSearch (MED-837).
  const [debouncedSearchState, setDebouncedSearchState] = useState<string>(search);
  const prevTabForSearchRef = useRef<TasksTab>(activeTab);
  const debouncedSearch = prevTabForSearchRef.current !== activeTab ? search : debouncedSearchState;
  useEffect(() => {
    if (prevTabForSearchRef.current !== activeTab) {
      prevTabForSearchRef.current = activeTab;
      setDebouncedSearchState(search);
      return;
    }
    const t = setTimeout(() => setDebouncedSearchState(search), 300);
    return () => clearTimeout(t);
  }, [search, activeTab]);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useTabState<string[]>(activeTab, { all: [], mine: [] });
  const [selectedCohortIds, setSelectedCohortIds] = useTabState<string[]>(activeTab, { all: [], mine: [] });
  const [selectedPhases, setSelectedPhases] = useTabState<string[]>(activeTab, { all: [], mine: [] });
  const [selectedStatuses, setSelectedStatuses] = useTabState<string[]>(activeTab, { all: [], mine: [] });
  const [sortBy, setSortBy] = useTabState<SortByField>(activeTab, { all: 'due_date', mine: 'due_date' });
  const [sortOrder, setSortOrder] = useTabState<SortDirection>(activeTab, { all: 'asc', mine: 'asc' });
  const [quickFilters, setQuickFilters] = useTabState<string[]>(
    activeTab,
    { all: createInitialTabState('all').quickFilters, mine: createInitialTabState('mine').quickFilters },
  );
  const [groupBy, setGroupBy] = useTabState<GroupLevel | null>(activeTab, { all: null, mine: null });
  const [expandedGroups, setExpandedGroups] = useTabState<Set<string>>(activeTab, { all: new Set(), mine: new Set() });
  const [addedFilterKeys, setAddedFilterKeys] = useTabState<Set<string>>(activeTab, { all: new Set(), mine: new Set() });
  const [page, setPage] = useTabState<number>(activeTab, { all: 1, mine: 1 });
  const [pageSize, setPageSize] = useTabState<number>(activeTab, { all: 50, mine: 50 });

  // Grouping data — fetched per active tab; not preserved across tab switches (refetched on switch)
  const [groupCounts, setGroupCounts] = useState<TaskGroupCount[]>([]);
  const [groupTasks, setGroupTasks] = useState<Record<string, CrossCohortTaskRow[]>>({});
  const [groupPages, setGroupPages] = useState<Record<string, number>>({});
  const [groupHasMore, setGroupHasMore] = useState<Record<string, boolean>>({});
  const [groupLoading, setGroupLoading] = useState<Record<string, boolean>>({});

  // MED-479: Subtask expansion
  const {
    expandedTaskIds,
    subtaskCache,
    loadingTaskIds,
    toggleTask,
    expandAll,
    collapseAll,
    expandTasks,
    invalidateCache,
    updateSubtaskInCache,
  } = useSubtaskExpansion();

  // MED-874: Auto-expand state for the Overdue status filter.
  // - On OFF→ON transition the user-collapsed tracker is reset so newly-revealed
  //   anchor rows expand fresh (AC12).
  // - Manual collapses during an active filter session are recorded so the
  //   auto-expand pass does not fight the user on subsequent data refreshes (AC11).
  // Initialized below from `isOverdueFilterActive` once it is in scope.
  const autoExpandStateRef = useRef<{
    filterWasActive: boolean;
    userCollapsed: Set<string>;
  }>({ filterWasActive: false, userCollapsed: new Set() });

  // Warning modal state for completing parent tasks with incomplete subtasks
  const [completeWarningOpened, setCompleteWarningOpened] = useState(false);
  const [pendingComplete, setPendingComplete] = useState<{ taskId: string; cohortId: string; currentStatus: TaskStatus } | null>(null);
  const [alsoCompleteSubtasks, setAlsoCompleteSubtasks] = useState(false);

  // Helper to toggle expand using task's cohort_id.
  // MED-874: Record user-initiated collapses during an active Overdue filter
  // session so the auto-expand pass below does not undo them on the next render.
  const handleToggleExpandFlat = useCallback((taskId: string) => {
    const state = autoExpandStateRef.current;
    if (state.filterWasActive && expandedTaskIds.has(taskId)) {
      state.userCollapsed.add(taskId);
    }
    const task = tasks.find((t) => t.id === taskId);
    if (task) toggleTask(taskId, task.cohort_id);
  }, [tasks, toggleTask, expandedTaskIds]);

  const handleToggleExpandGrouped = useCallback((taskId: string, groupKey: string) => {
    const state = autoExpandStateRef.current;
    if (state.filterWasActive && expandedTaskIds.has(taskId)) {
      state.userCollapsed.add(taskId);
    }
    const task = (groupTasks[groupKey] || []).find((t: CrossCohortTaskRow) => t.id === taskId);
    if (task) toggleTask(taskId, task.cohort_id);
  }, [groupTasks, toggleTask, expandedTaskIds]);

  // MED-769: Expand all / collapse all for cross-cohort tasks
  const handleExpandAll = useCallback(() => {
    // Collect all parent tasks from both flat and grouped views
    const allTasks = groupBy
      ? Object.values(groupTasks).flat()
      : tasks;
    expandAll(
      allTasks.map((t) => ({ id: t.id, cohortId: t.cohort_id, subtaskCount: t.subtask_count })),
    );
  }, [groupBy, groupTasks, tasks, expandAll]);

  // Check if there are any parent tasks (to show/hide the button)
  // and whether ALL parent tasks are expanded (for toggle label)
  const parentTaskIds = useMemo(() => {
    const allTasks = groupBy
      ? Object.values(groupTasks).flat()
      : tasks;
    return allTasks.filter((t) => t.subtask_count > 0).map((t) => t.id);
  }, [groupBy, groupTasks, tasks]);

  const hasParentTasks = parentTaskIds.length > 0;
  // AC4: Mixed state (some expanded, some not) should show "Expand all"
  const allParentsExpanded = hasParentTasks && parentTaskIds.every((id) => expandedTaskIds.has(id));

  // MED-874: The Overdue status filter auto-expands any parent row whose subtask
  // is overdue (AC4). Flatten mode is reserved for My Tasks (MED-753 / MED-837);
  // the Overdue filter now uses true nested auto-expansion so subtasks render
  // under their expanded parents via the existing chevron path.
  const isOverdueFilterActive = selectedStatuses.includes('overdue');
  const isFlattenedView = activeTab === 'mine';

  // MED-874: Drive the auto-expand pass. Detects the OFF→ON transition for the
  // Overdue filter (or initial mount with the filter already active) and expands
  // every parent in the loaded task set whose `has_overdue_subtasks` flag is true.
  // Manual collapses during the filter session are honored via `userCollapsed`
  // recorded by `handleToggleExpandFlat` / `handleToggleExpandGrouped`. The
  // pass is idempotent — `expandTasks` is set-additive, so re-runs on data
  // refresh (e.g., search, group page-in) do not fight already-expanded rows
  // and skip parents the user has explicitly collapsed (AC10, AC11).
  useEffect(() => {
    const state = autoExpandStateRef.current;
    if (isOverdueFilterActive && !state.filterWasActive) {
      state.userCollapsed = new Set();
    }
    state.filterWasActive = isOverdueFilterActive;

    if (!isOverdueFilterActive) return;

    const visibleTasks: CrossCohortTaskRow[] = groupBy
      ? Object.values(groupTasks).flat()
      : tasks;

    const parentsToExpand = visibleTasks
      .filter(
        (t) =>
          t.has_overdue_subtasks &&
          t.subtask_count > 0 &&
          !state.userCollapsed.has(t.id) &&
          !expandedTaskIds.has(t.id),
      )
      .map((t) => ({ id: t.id, cohortId: t.cohort_id, subtaskCount: t.subtask_count }));

    if (parentsToExpand.length > 0) {
      expandTasks(parentsToExpand);
    }
  }, [isOverdueFilterActive, tasks, groupTasks, groupBy, expandTasks, expandedTaskIds]);

  // Handle subtask click in cross-cohort view - open drawer
  const handleSubtaskClick = useCallback((subtask: TaskWithDetails, cohortId: string, cohortName: string) => {
    setSelectedTaskId(subtask.id);
    setSelectedTaskCohortId(cohortId);
    setSelectedTaskCohortName(cohortName);
    setDrawerOpened(true);
    setSearchParams({ taskId: subtask.id, cohortId }, { replace: true });
  }, [setSearchParams]);

  // Handle toggle task completion in cross-cohort view
  const executeToggleComplete = useCallback(async (taskId: string, cohortId: string, currentStatus: TaskStatus, completeSubtasks?: boolean) => {
    const newStatus: TaskStatus = currentStatus === 'complete' ? 'not_started' : 'complete';

    // Optimistically update UI (flat tasks + grouped tasks + subtask cache)
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus, ...(completeSubtasks && newStatus === 'complete' ? { subtasks_completed: t.subtask_count } : {}) } : t))
    );
    setGroupTasks((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        next[key] = next[key].map((t) =>
          t.id === taskId ? { ...t, status: newStatus, ...(completeSubtasks && newStatus === 'complete' ? { subtasks_completed: t.subtask_count } : {}) } : t
        );
      }
      return next;
    });
    updateSubtaskInCache(taskId, { status: newStatus });

    // If completing subtasks too, optimistically mark all cached subtasks as complete
    if (completeSubtasks && newStatus === 'complete' && subtaskCache[taskId]) {
      for (const subtask of subtaskCache[taskId]) {
        if (subtask.status !== 'complete') {
          updateSubtaskInCache(subtask.id, { status: 'complete' });
        }
      }
    }

    try {
      await tasksApi.updateTaskStatus(cohortId, taskId, newStatus, completeSubtasks);
      notifications.show({
        title: 'Task Updated',
        message: completeSubtasks
          ? 'Task and subtasks marked as Complete'
          : `Task marked as ${TASK_STATUS_CONFIG[newStatus].label}`,
        color: 'green',
      });
    } catch (error) {
      console.error('Failed to update task status:', error);
      // Rollback
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: currentStatus } : t))
      );
      setGroupTasks((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = next[key].map((t) =>
            t.id === taskId ? { ...t, status: currentStatus } : t
          );
        }
        return next;
      });
      updateSubtaskInCache(taskId, { status: currentStatus });
      notifications.show({
        title: 'Error',
        message: 'Failed to update task',
        color: 'red',
      });
    }
  }, [updateSubtaskInCache, subtaskCache]);

  const handleToggleComplete = useCallback((taskId: string, cohortId: string, currentStatus: TaskStatus) => {
    // Check if completing a parent task with incomplete subtasks
    if (currentStatus !== 'complete') {
      const task = tasks.find((t) => t.id === taskId)
        || Object.values(groupTasks).flat().find((t) => t.id === taskId);
      if (task && task.subtask_count > 0 && task.subtasks_completed < task.subtask_count) {
        setPendingComplete({ taskId, cohortId, currentStatus });
        setCompleteWarningOpened(true);
        return;
      }
    }
    executeToggleComplete(taskId, cohortId, currentStatus);
  }, [tasks, groupTasks, executeToggleComplete]);

  // ── Derived filter panel props ─────────────────────────────────────────

  // Build filter field configs (options depend on loaded data)
  const filterFields = useMemo((): FilterFieldConfig[] => {
    const cohortOptions = cohorts.map((c) => ({ value: c.id, label: c.name }));
    return [
      { key: 'status', label: 'Status', type: 'multiselect', options: STATUS_OPTIONS },
      { key: 'phase', label: 'Phase', type: 'multiselect', options: TASK_PHASE_OPTIONS },
      { key: 'cohort', label: 'Cohort', type: 'multiselect', options: cohortOptions },
    ];
  }, [cohorts]);

  // Active filter rows derived from existing state + explicitly added rows
  const activeFilters = useMemo((): ActiveFilterRow[] => {
    const rows: ActiveFilterRow[] = [];
    if (selectedStatuses.length > 0 || addedFilterKeys.has('status')) {
      rows.push({ key: 'status', values: selectedStatuses });
    }
    if (selectedPhases.length > 0 || addedFilterKeys.has('phase')) {
      rows.push({ key: 'phase', values: selectedPhases });
    }
    if (selectedCohortIds.length > 0 || addedFilterKeys.has('cohort')) {
      rows.push({ key: 'cohort', values: selectedCohortIds });
    }
    return rows;
  }, [selectedStatuses, selectedPhases, selectedCohortIds, addedFilterKeys]);

  // Quick filter state for panel — already an array (multi-select)
  const activeQuickFilters = quickFilters;

  // Active sort for SortPanel
  const activeSort = useMemo((): SortState => ({ field: sortBy, direction: sortOrder }), [sortBy, sortOrder]);

  // Check if any filters are active (including non-default sort and group)
  // For My Tasks, "default" includes the always-on Incomplete quick filter — so a
  // state where only Incomplete is active doesn't count as "active filters".
  const hasActiveFilters = useMemo(() => {
    const initial = createInitialTabState(activeTab);
    const quickFiltersChanged =
      quickFilters.length !== initial.quickFilters.length ||
      quickFilters.some((k) => !initial.quickFilters.includes(k));
    return (
      debouncedSearch !== '' ||
      selectedAssigneeIds.length > 0 ||
      selectedCohortIds.length > 0 ||
      selectedPhases.length > 0 ||
      selectedStatuses.length > 0 ||
      quickFiltersChanged ||
      sortBy !== 'due_date' ||
      sortOrder !== 'asc' ||
      groupBy !== null
    );
  }, [
    activeTab,
    debouncedSearch,
    selectedAssigneeIds,
    selectedCohortIds,
    selectedPhases,
    selectedStatuses,
    quickFilters,
    sortBy,
    sortOrder,
    groupBy,
  ]);

  // ── Handlers ───────────────────────────────────────────────────────────

  // MED-837: Clear resets the active tab to its initial state. For My Tasks,
  // that re-arms the default-active "Incomplete" quick filter (AC18).
  const clearFilters = useCallback(() => {
    const initial = createInitialTabState(activeTab);
    setSearch(initial.search);
    setSelectedAssigneeIds(initial.selectedAssigneeIds);
    setSelectedCohortIds(initial.selectedCohortIds);
    setSelectedPhases(initial.selectedPhases);
    setSelectedStatuses(initial.selectedStatuses);
    setQuickFilters(initial.quickFilters);
    setSortBy(initial.sortBy);
    setSortOrder(initial.sortOrder);
    setGroupBy(initial.groupBy);
    setExpandedGroups(initial.expandedGroups);
    setGroupCounts([]);
    setGroupTasks({});
    setGroupPages({});
    setGroupHasMore({});
    setGroupLoading({});
    setAddedFilterKeys(initial.addedFilterKeys);
    setPage(initial.page);
  }, [
    activeTab,
    setSearch,
    setSelectedAssigneeIds,
    setSelectedCohortIds,
    setSelectedPhases,
    setSelectedStatuses,
    setQuickFilters,
    setSortBy,
    setSortOrder,
    setGroupBy,
    setExpandedGroups,
    setAddedFilterKeys,
    setPage,
  ]);

  // FilterPanel handlers
  const handleFilterAdd = useCallback((key: string) => {
    setAddedFilterKeys((prev) => new Set(prev).add(key));
  }, [setAddedFilterKeys]);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    if (key === 'status') setSelectedStatuses(values);
    else if (key === 'phase') setSelectedPhases(values);
    else if (key === 'cohort') setSelectedCohortIds(values);
  }, [setSelectedStatuses, setSelectedPhases, setSelectedCohortIds]);

  const handleFilterRemove = useCallback((key: string) => {
    if (key === 'status') setSelectedStatuses([]);
    else if (key === 'phase') setSelectedPhases([]);
    else if (key === 'cohort') setSelectedCohortIds([]);
    setAddedFilterKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, [setSelectedStatuses, setSelectedPhases, setSelectedCohortIds, setAddedFilterKeys]);

  const handleFilterClearAll = useCallback(() => {
    setSelectedStatuses([]);
    setSelectedPhases([]);
    setSelectedCohortIds([]);
    setQuickFilters([]);
    setAddedFilterKeys(new Set());
  }, [setSelectedStatuses, setSelectedPhases, setSelectedCohortIds, setQuickFilters, setAddedFilterKeys]);

  const handleQuickFilterToggle = useCallback((key: string) => {
    setQuickFilters((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, [setQuickFilters]);

  const handleSortChange = useCallback((sort: SortState | null) => {
    if (sort) {
      setSortBy(sort.field as SortByField);
      setSortOrder(sort.direction);
    } else {
      setSortBy('due_date');
      setSortOrder('asc');
    }
  }, [setSortBy, setSortOrder]);

  // Tasks group on the server by one field, so the panel offers one level only
  const handleGroupChange = useCallback((group: GroupState | null) => {
    setGroupBy(group?.[0] ?? null);
    setExpandedGroups(new Set());
  }, [setGroupBy, setExpandedGroups]);

  // Build base filters object (shared between flat fetch & group counts)
  const buildBaseFilters = useCallback((): CrossCohortTaskFilters => {
    const filters: CrossCohortTaskFilters = {
      sortBy,
      sortOrder,
    };

    if (debouncedSearch) {
      filters.search = debouncedSearch;
    }

    // Status filter — separate 'overdue' (computed) from real statuses.
    // MED-837 (AC18): the "Incomplete" quick filter applies INCOMPLETE_TASK_STATUSES
    // when no explicit status filter is selected; an explicit status filter row wins.
    if (selectedStatuses.length > 0) {
      const hasOverdue = selectedStatuses.includes('overdue');
      const realStatuses = selectedStatuses.filter((s) => s !== 'overdue') as TaskStatus[];

      if (hasOverdue) {
        filters.isOverdue = true;
        // MED-874: The cross-cohort response stays nested for the Overdue filter.
        // The backend returns directly-overdue parents AND parent-anchor rows
        // (parents whose subtasks are overdue); the frontend auto-expands those
        // anchor rows so the overdue subtasks render inline via the existing
        // chevron path.
      }
      if (realStatuses.length > 0) {
        filters.status = realStatuses;
      }
    } else if (quickFilters.includes('incomplete')) {
      filters.status = INCOMPLETE_TASK_STATUSES;
    }

    if (selectedPhases.length > 0) {
      filters.phase = selectedPhases;
    }
    if (selectedCohortIds.length > 0) {
      filters.cohortIds = selectedCohortIds;
    }

    // Assignee filter.
    // MED-837 (AC8/AC17/AC20): My Tasks is always scoped to the current user.
    // The constraint is injected here regardless of any user-controllable filter
    // state, so a user can never see another user's tasks via this tab.
    if (activeTab === 'mine' && user?.id) {
      filters.assigneeIds = [user.id];
    } else if (selectedAssigneeIds.length > 0) {
      filters.assigneeIds = selectedAssigneeIds;
    }

    // Handle "Due This Week" quick filter (includes overdue tasks)
    if (quickFilters.includes('due_this_week')) {
      const { from, to } = getThisWeekRange();
      filters.dueDateFrom = from;
      filters.dueDateTo = to;
      filters.includeOverdue = true;
    }

    return filters;
  }, [
    activeTab,
    user?.id,
    sortBy,
    sortOrder,
    debouncedSearch,
    selectedStatuses,
    selectedPhases,
    selectedCohortIds,
    selectedAssigneeIds,
    quickFilters,
  ]);

  const GROUP_PAGE_SIZE = 50;

  // Fetch tasks (flat mode) or group counts (grouped mode)
  // MED-837: My Tasks tab now flows through the same pipeline as All Tasks —
  // the assignee=current-user constraint is injected by buildBaseFilters (AC8/AC17),
  // and subtasks are flattened via the flatten=true flag below (AC19).
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);

      // My Tasks requires the user to be loaded — bail out gracefully otherwise.
      if (activeTab === 'mine' && !user?.id) {
        setTasks([]);
        setTotalTasks(0);
        setGroupCounts([]);
        return;
      }

      const baseFilters = buildBaseFilters();

      if (groupBy) {
        // Grouped mode: fetch only group counts (lightweight)
        const counts = await tasksApi.getGroupCounts(baseFilters, groupBy.field as TaskGroupByField);
        setGroupCounts(counts);
        setTotalTasks(counts.reduce((sum, g) => sum + g.count, 0));
        // Reset per-group state
        setGroupTasks({});
        setGroupPages({});
        setGroupHasMore({});
        setGroupLoading({});
        // Auto-expand first group
        if (counts.length > 0) {
          setExpandedGroups(new Set([counts[0].key]));
        } else {
          setExpandedGroups(new Set());
        }
        setTasks([]);
      } else {
        // Flat mode: paginated task fetch
        const filters: CrossCohortTaskFilters = {
          ...baseFilters,
          page,
          limit: pageSize,
          // AC19: My Tasks renders subtasks flattened with parent context
          ...(activeTab === 'mine' ? { flatten: true } : {}),
        };
        const response = await tasksApi.getAllTasks(filters);
        setTasks(response.data);
        setTotalTasks(response.pagination.total);
        setGroupCounts([]);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load tasks',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [activeTab, user?.id, buildBaseFilters, groupBy, page, pageSize, setExpandedGroups]);

  // Fetch tasks for a specific group (called when expanding or showing more)
  const fetchGroupTasks = useCallback(async (groupKey: string, pageNum: number) => {
    if (!groupBy) return;

    setGroupLoading((prev) => ({ ...prev, [groupKey]: true }));
    try {
      const baseFilters = buildBaseFilters();

      // Add the group-specific filter to narrow results to this group
      const groupFilters: CrossCohortTaskFilters = { ...baseFilters };
      switch (groupBy.field) {
        case 'cohort':
          // Find cohort ID by name
          groupFilters.cohortIds = cohorts
            .filter((c) => c.name === groupKey)
            .map((c) => c.id);
          break;
        case 'phase':
          groupFilters.phase = groupKey === 'No Phase' ? [] : [groupKey];
          break;
        case 'status':
          groupFilters.status = [groupKey as TaskStatus];
          break;
        case 'assignee':
          if (groupKey === 'Unassigned') {
            groupFilters.assigneeIds = ['unassigned'];
          } else {
            // Find assignee ID by name
            const assignee = assignees.find(
              (a) => `${a.first_name} ${a.last_name}` === groupKey,
            );
            if (assignee) groupFilters.assigneeIds = [assignee.id];
          }
          break;
        case 'partner':
          // Partner filter not directly supported — we'll pass it as-is and let server handle
          break;
      }

      groupFilters.page = pageNum;
      groupFilters.limit = GROUP_PAGE_SIZE;
      // AC19: My Tasks renders subtasks flattened with parent context, even within groups.
      if (activeTab === 'mine') {
        groupFilters.flatten = true;
      }

      const response = await tasksApi.getAllTasks(groupFilters);

      setGroupTasks((prev) => ({
        ...prev,
        [groupKey]: pageNum === 1
          ? response.data
          : [...(prev[groupKey] || []), ...response.data],
      }));
      setGroupPages((prev) => ({ ...prev, [groupKey]: pageNum }));
      setGroupHasMore((prev) => ({
        ...prev,
        [groupKey]: response.data.length === GROUP_PAGE_SIZE,
      }));
    } catch (error) {
      console.error(`Failed to fetch tasks for group "${groupKey}":`, error);
    } finally {
      setGroupLoading((prev) => ({ ...prev, [groupKey]: false }));
    }
  }, [activeTab, groupBy, buildBaseFilters, cohorts, assignees]);

  // Fetch assignees and cohorts on mount
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [assigneesData, cohortsData] = await Promise.all([
          tasksApi.getTaskAssignees(),
          cohortsApi.list({ limit: 100 }),
        ]);
        setAssignees(assigneesData);
        setCohorts(cohortsData.data);
      } catch (error) {
        console.error('Failed to fetch filter data:', error);
      }
    };
    fetchFilterData();
  }, []);

  // Fetch tasks when filters change
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Reset page to 1 when filters change on the active tab.
  // MED-837: skip reset on tab-switch — switching tabs preserves each tab's
  // own pagination state (AC15/AC16).
  const prevActiveTabRef = useRef(activeTab);
  useEffect(() => {
    if (prevActiveTabRef.current !== activeTab) {
      prevActiveTabRef.current = activeTab;
      return;
    }
    setPage(1);
  }, [
    debouncedSearch,
    selectedAssigneeIds,
    selectedCohortIds,
    selectedPhases,
    selectedStatuses,
    quickFilters,
    sortBy,
    sortOrder,
    activeTab,
    setPage,
  ]);

  // Convert TaskAssignee[] to AvatarFilterItem[] for shared component
  const assigneeItems = useMemo((): AvatarFilterItem[] => {
    return assignees.map((a) => ({
      id: a.id,
      name: `${a.first_name} ${a.last_name}`,
      colorKey: a.id,
    }));
  }, [assignees]);

  // Handle assignee avatar click — used by AvatarFilterBar (All Tasks tab only).
  const handleAssigneeClick = (assigneeId: string) => {
    setSelectedAssigneeIds((prev) => {
      if (prev.includes(assigneeId)) {
        return prev.filter((id) => id !== assigneeId);
      }
      return [...prev, assigneeId];
    });
  };

  // Handle row click - open task detail drawer
  const handleRowClick = useCallback((task: CrossCohortTaskRow) => {
    setSelectedTaskId(task.id);
    setSelectedTaskCohortId(task.cohort_id);
    setSelectedTaskCohortName(task.cohort_name);
    setDrawerOpened(true);
    setSearchParams({ taskId: task.id, cohortId: task.cohort_id }, { replace: true });
  }, [setSearchParams]);

  // Handle drawer close
  const handleDrawerClose = useCallback(() => {
    setDrawerOpened(false);
    setSelectedTaskId(null);
    setSelectedTaskCohortId('');
    setSelectedTaskCohortName('');
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // MED-770: Auto-open drawer from deep link URL params
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    const taskIdParam = searchParams.get('taskId');
    const cohortIdParam = searchParams.get('cohortId');
    if (taskIdParam && cohortIdParam && !deepLinkHandledRef.current && !loading) {
      deepLinkHandledRef.current = true;
      setSelectedTaskId(taskIdParam);
      setSelectedTaskCohortId(cohortIdParam);
      // cohortName will be empty for deep links — TaskDetailDrawer fetches its own data
      setSelectedTaskCohortName('');
      setDrawerOpened(true);
    }
  }, [searchParams, loading]);

  // ── Group expand/collapse ─────────────────────────────────────────────

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, [setExpandedGroups]);

  // Fetch group tasks when a group is expanded (lazy loading)
  useEffect(() => {
    if (!groupBy) return;
    expandedGroups.forEach((key) => {
      if (!groupTasks[key] && !groupLoading[key]) {
        fetchGroupTasks(key, 1);
      }
    });
  }, [expandedGroups, groupBy, groupTasks, groupLoading, fetchGroupTasks]);

  const handleShowMore = useCallback((groupKey: string) => {
    const nextPage = (groupPages[groupKey] || 1) + 1;
    fetchGroupTasks(groupKey, nextPage);
  }, [groupPages, fetchGroupTasks]);

  // Calculate display counts
  const displayedCount = groupBy
    ? Object.values(groupTasks).reduce((sum, t) => sum + t.length, 0)
    : tasks.length;

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <Container size="xl" py="xl">
      {/* Page Header */}
      <Stack gap="xs" mb="md">
        <Title order={1}>Tasks</Title>
        <Text c="dimmed" size="sm">
          {totalTasks} total tasks · {displayedCount} showing
        </Text>
      </Stack>

      {/* MED-753: Top-level tabs ('All' default, 'My Tasks' shows assigned + incomplete + flattened) */}
      <Tabs
        value={activeTab}
        onChange={(value) => setActiveTab((value as TasksTab) || 'all')}
        mb="lg"
      >
        <Tabs.List>
          <Tabs.Tab value="all">All</Tabs.Tab>
          <Tabs.Tab value="mine">My Tasks</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {/* MED-837: Filter / Sort / Group toolbar — present on both tabs.
          AvatarFilterBar is omitted on My Tasks (AC20) since the tab is already
          scoped to the current user. Group fields exclude "Assignee" on My Tasks
          (AC12) since every task has the same assignee. The Expand all / Collapse
          all toggle is hidden on My Tasks because subtasks render flattened (AC19). */}
      <Group justify="space-between" wrap="wrap" mb="lg">
        <Group gap="sm" wrap="wrap">
          {/* Search Input */}
          <TextInput
            placeholder="Search tasks..."
            leftSection={<Search size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            w={220}
            size="sm"
          />

          {/* Assignee Avatars — All Tasks only (AC20) */}
          {activeTab === 'all' && assigneeItems.length > 0 && (
            <>
              <AvatarFilterBar
                items={assigneeItems}
                selectedIds={selectedAssigneeIds}
                onToggle={handleAssigneeClick}
                maxVisible={5}
                size={36}
                showUnassigned
              />
              <Divider orientation="vertical" className={classes.filterDivider} />
            </>
          )}

          {/* Filter Panel */}
          <FilterPanel
            quickFilters={QUICK_FILTERS}
            activeQuickFilters={activeQuickFilters}
            onQuickFilterToggle={handleQuickFilterToggle}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterAdd={handleFilterAdd}
            onFilterChange={handleFilterChange}
            onFilterRemove={handleFilterRemove}
            onClearAll={handleFilterClearAll}
          />

          {/* Sort Panel */}
          <SortPanel
            sortFields={SORT_FIELDS}
            activeSort={activeSort}
            defaultSort={{ field: 'due_date', direction: 'asc' }}
            onSortChange={handleSortChange}
          />

          {/* Group Panel — "Assignee" omitted on My Tasks (AC12) */}
          <GroupPanel
            groupFields={activeTab === 'mine'
              ? GROUP_FIELDS.filter((f) => f.value !== 'assignee')
              : GROUP_FIELDS}
            activeGroup={groupBy ? [groupBy] : null}
            defaultGroup={null}
            onGroupChange={handleGroupChange}
            maxLevels={1}
          />

          {/* Clear Button */}
          {hasActiveFilters && (
            <Button
              variant="subtle"
              color="gray"
              size="sm"
              leftSection={<X size={14} />}
              onClick={clearFilters}
            >
              Clear
            </Button>
          )}
        </Group>

        {/* Expand All / Collapse All toggle — hidden in flattened views (My Tasks only; the Overdue filter uses nested auto-expansion per MED-874) */}
        {!isFlattenedView && hasParentTasks && (
          <Button
            variant="default"
            size="xs"
            onClick={allParentsExpanded ? collapseAll : handleExpandAll}
          >
            {allParentsExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        )}
      </Group>

      {/* Tasks List */}
      {loading ? (
        <Center py={100}>
          <Loader />
        </Center>
      ) : groupBy && groupCounts.length === 0 ? (
        <Paper withBorder radius="md" p="xl">
          <EmptyState
            icon={<CheckSquare size={28} />}
            title="No tasks match your filters"
            description="Try adjusting your search or filter criteria"
            action={
              <Button variant="light" color="gray" onClick={clearFilters}>
                Clear all filters
              </Button>
            }
          />
        </Paper>
      ) : !groupBy && tasks.length === 0 ? (
        <Paper withBorder radius="md" p="xl">
          <EmptyState
            icon={<CheckSquare size={28} />}
            title="No tasks match your filters"
            description="Try adjusting your search or filter criteria"
            action={
              <Button variant="light" color="gray" onClick={clearFilters}>
                Clear all filters
              </Button>
            }
          />
        </Paper>
      ) : groupBy ? (
        // ── Grouped rendering (lazy per-group fetching) ───────────────
        <Stack gap="xs">
          {groupCounts.map((group) => (
            <GroupSection
              key={group.key}
              groupKey={group.key}
              label={group.label}
              count={group.count}
              tasks={groupTasks[group.key] || []}
              expanded={expandedGroups.has(group.key)}
              onToggle={() => toggleGroup(group.key)}
              onRowClick={handleRowClick}
              hasMore={groupHasMore[group.key] ?? true}
              isLoading={groupLoading[group.key] ?? false}
              onShowMore={() => handleShowMore(group.key)}
              expandedTaskIds={expandedTaskIds}
              loadingTaskIds={loadingTaskIds}
              subtaskCache={subtaskCache}
              onToggleExpand={isFlattenedView ? undefined : (taskId: string) => handleToggleExpandGrouped(taskId, group.key)}
              onSubtaskClick={handleSubtaskClick}
              onToggleComplete={handleToggleComplete}
              isCompact={isCompact}
            />
          ))}
        </Stack>
      ) : (
        // ── Flat rendering ───────────────────────────────────────────────
        <Paper
          withBorder
          radius="md"
          styles={{ root: { boxShadow: 'none' } }}
        >
          {/* Column headers */}
          <Group
            gap="xl"
            px="lg"
            py="sm"
            className={classes.columnHeaders}
          >
            <Box w={34} /> {/* chevron spacer */}
            <Box w={20} /> {/* checkbox spacer */}
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ width: TASK_COL_WIDTH, flexShrink: 0, letterSpacing: '0.5px' }}>
              Task
            </Text>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ width: 180, flexShrink: 0, letterSpacing: '0.5px' }}>
              Cohort
            </Text>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ width: 170, flexShrink: 0, letterSpacing: '0.5px' }}>
              Phase
            </Text>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ width: 110, flexShrink: 0, letterSpacing: '0.5px' }}>
              Due Date
            </Text>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ width: assigneeColWidth, flexShrink: 0, letterSpacing: '0.5px' }}>
              Assignee
            </Text>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ width: 110, flexShrink: 0, letterSpacing: '0.5px', textAlign: 'right' }}>
              Status
            </Text>
          </Group>
          <Divider />
          <Stack gap={0}>
            {tasks.map((task) => (
              <CrossCohortTaskListRow
                key={task.id}
                task={task}
                onClick={handleRowClick}
                onToggleComplete={handleToggleComplete}
                isExpanded={expandedTaskIds.has(task.id)}
                isLoadingSubtasks={loadingTaskIds.has(task.id)}
                subtasks={subtaskCache[task.id]}
                // MED-753: My Tasks flattens subtasks — suppress chevron + inline expansion.
                // MED-874: Overdue filter keeps the chevron and uses auto-expansion instead.
                onToggleExpand={isFlattenedView ? undefined : handleToggleExpandFlat}
                onSubtaskClick={(subtask) => handleSubtaskClick(subtask, task.cohort_id, task.cohort_name)}
                isCompact={isCompact}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* Pagination (hidden when grouping is active — groups handle their own "show more") */}
      {!loading && tasks.length > 0 && !groupBy && (
        <Box mt="md">
          <Pagination
            page={page}
            pageSize={pageSize}
            total={totalTasks}
            totalPages={Math.ceil(totalTasks / pageSize)}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </Box>
      )}

      {/* Task Detail Drawer */}
      <TaskDetailDrawer
        cohortId={selectedTaskCohortId}
        taskId={selectedTaskId}
        opened={drawerOpened}
        onClose={handleDrawerClose}
        onUpdated={() => {
          fetchTasks();
          // MED-479: Invalidate subtask cache for the selected task
          if (selectedTaskId) {
            invalidateCache(selectedTaskId);
          }
        }}
        cohortName={selectedTaskCohortName}
      />

      {/* MED-479: Warning modal for completing parent with incomplete subtasks */}
      <Modal
        opened={completeWarningOpened}
        onClose={() => {
          setCompleteWarningOpened(false);
          setPendingComplete(null);
          setAlsoCompleteSubtasks(false);
        }}
        title="Incomplete Subtasks"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This task has incomplete subtasks. Are you sure you want to complete this task?
          </Text>
          <Checkbox
            label="Also complete all incomplete subtasks"
            checked={alsoCompleteSubtasks}
            onChange={(event) => setAlsoCompleteSubtasks(event.currentTarget.checked)}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                setCompleteWarningOpened(false);
                setPendingComplete(null);
                setAlsoCompleteSubtasks(false);
              }}
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              onClick={() => {
                if (pendingComplete) {
                  executeToggleComplete(pendingComplete.taskId, pendingComplete.cohortId, pendingComplete.currentStatus, alsoCompleteSubtasks);
                }
                setCompleteWarningOpened(false);
                setPendingComplete(null);
                setAlsoCompleteSubtasks(false);
              }}
            >
              Complete Task
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

// ── Task Row (matches cohort detail TaskListRow style) ───────────────────
// MED-479: Added inline subtask expansion

function CrossCohortTaskListRow({
  task,
  onClick,
  onToggleComplete,
  isExpanded = false,
  isLoadingSubtasks = false,
  subtasks,
  onToggleExpand,
  onSubtaskClick,
  isCompact = false,
}: {
  task: CrossCohortTaskRow;
  onClick: (task: CrossCohortTaskRow) => void;
  onToggleComplete?: (taskId: string, cohortId: string, currentStatus: TaskStatus) => void;
  isExpanded?: boolean;
  isLoadingSubtasks?: boolean;
  subtasks?: TaskWithDetails[];
  onToggleExpand?: (taskId: string) => void;
  onSubtaskClick?: (subtask: TaskWithDetails) => void;
  isCompact?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const isComplete = task.status === 'complete';
  const statusConfig = TASK_STATUS_CONFIG[task.status];
  const overdue = isTaskOverdue(task);
  const hasSubtasks = task.subtask_count > 0;

  const avatarColor = task.assignee_id
    ? getAvatarColor(task.assignee_id)
    : getAvatarColor('unassigned');

  // MED-753: compact-mode assignee column (avatar only)
  const assigneeColWidth = isCompact ? ASSIGNEE_COL_WIDTH_COMPACT : ASSIGNEE_COL_WIDTH_DEFAULT;

  return (
    <>
      <Box
        className={classes.taskRow}
        style={{
          borderLeft: isHovered ? '3px solid #28463A' : '3px solid transparent',
          backgroundColor: isHovered ? '#F5F7F6' : (overdue ? 'rgba(214, 69, 69, 0.06)' : 'transparent'),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => onClick(task)}
      >
        <Group
          gap="xl"
          py="sm"
          px="lg"
          style={{
            paddingLeft: isHovered ? 'calc(var(--mantine-spacing-lg) - 3px)' : 'var(--mantine-spacing-lg)',
          }}
        >
          {/* Expand/Collapse Chevron */}
          {hasSubtasks && onToggleExpand ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(task.id);
              }}
            >
              {isExpanded ? (
                <ChevronDown size={16} color="var(--mantine-color-dimmed)" />
              ) : (
                <ChevronRight size={16} color="var(--mantine-color-dimmed)" />
              )}
            </ActionIcon>
          ) : (
            <Box w={34} />
          )}

          {/* Checkbox */}
          <Checkbox
            checked={isComplete}
            color="medleyGreen"
            size="sm"
            onChange={(e) => {
              e.stopPropagation();
              onToggleComplete?.(task.id, task.cohort_id, task.status);
            }}
            onClick={(e) => e.stopPropagation()}
            styles={{ input: { cursor: 'pointer' } }}
          />

          {/* Task Title + Copy Link */}
          <Stack gap={2} style={{ width: TASK_COL_WIDTH, flexShrink: 0 }}>
            {/* MED-753: when this row is a flattened subtask, show parent task name above the title */}
            {task.parent_task_id && task.parent_task_title && (
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {task.parent_task_title}
              </Text>
            )}
            <Group gap={8} wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
              <Text
                size="sm"
                style={{
                  textDecoration: isComplete ? 'line-through' : 'none',
                  color: isComplete ? 'var(--mantine-color-dimmed)' : '#28463A',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {task.title}
              </Text>
              {hasSubtasks && onToggleExpand && (
                <Text size="xs" c="dimmed">
                  ({task.subtask_count})
                </Text>
              )}
            {/* MED-770: Copy link — positioned right of title */}
            {isHovered && (
              <Tooltip label="Copy link to task" position="top" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyTaskLink(task.id, task.cohort_id, 'cross-cohort');
                  }}
                >
                  <LinkIcon size={14} />
                </ActionIcon>
              </Tooltip>
            )}
            </Group>
          </Stack>

          {/* Cohort */}
          <Text size="xs" style={{ width: 180, flexShrink: 0, color: '#28463A' }} truncate>
            {task.cohort_name}
          </Text>

          {/* Phase */}
          <Text size="xs" c="dimmed" style={{ width: 170, flexShrink: 0 }} truncate>
            {task.phase_name || '—'}
          </Text>

          {/* Due Date */}
          <Box style={{ width: 110, flexShrink: 0 }}>
            {task.due_date ? (
              <Group gap={4} wrap="nowrap">
                <Calendar size={12} color={overdue ? '#D64545' : '#555555'} />
                <Text
                  size="xs"
                  fw={overdue ? 600 : 400}
                  style={{ color: overdue ? '#D64545' : '#555555' }}
                >
                  {formatDateOnly(task.due_date)}
                </Text>
              </Group>
            ) : (
              <Text size="xs" c="dimmed">—</Text>
            )}
          </Box>

          {/* Assignee — name hidden in compact mode (avatar only) */}
          <Box style={{ width: assigneeColWidth, flexShrink: 0 }}>
            {task.assignee_name ? (
              <Group gap={6} wrap="nowrap">
                <Tooltip label={task.assignee_name} disabled={!isCompact} withArrow>
                  <Avatar
                    size="sm"
                    radius="xl"
                    styles={{
                      root: { backgroundColor: avatarColor },
                      placeholder: { backgroundColor: avatarColor, color: '#FFFFFF' },
                    }}
                  >
                    {getInitials(task.assignee_name)}
                  </Avatar>
                </Tooltip>
                {!isCompact && (
                  <Text size="xs" style={{ color: '#28463A' }} truncate>
                    {task.assignee_name.split(' ')[0]}
                  </Text>
                )}
              </Group>
            ) : isCompact ? (
              <Tooltip label="Unassigned" withArrow>
                <Avatar
                  size="sm"
                  radius="xl"
                  styles={{
                    root: { backgroundColor: avatarColor },
                    placeholder: { backgroundColor: avatarColor, color: '#FFFFFF' },
                  }}
                />
              </Tooltip>
            ) : (
              <Text size="xs" style={{ color: '#555555' }}>Unassigned</Text>
            )}
          </Box>

          {/* Status Badges — stacked vertically and right-aligned to match the STATUS header */}
          <Stack gap={4} align="flex-end" style={{ width: 110, flexShrink: 0 }}>
            {overdue ? (
              <Badge
                size="xs"
                color="dangerRed"
                variant="filled"
                styles={{ root: { minWidth: 70 } }}
              >
                OVERDUE
              </Badge>
            ) : (
              <Badge
                size="xs"
                color={statusConfig.color}
                variant={statusConfig.variant}
                styles={{ root: { minWidth: 70, ...(task.status === 'not_started' ? { border: 'none', color: '#888888' } : {}) } }}
              >
                {statusConfig.label.toUpperCase()}
              </Badge>
            )}

            {/* Subtasks Overdue Badge - only on parent tasks with overdue subtasks */}
            {task.has_overdue_subtasks && (
              <Badge
                size="xs"
                color="dangerRed"
                variant="light"
              >
                SUBTASKS OVERDUE
              </Badge>
            )}
          </Stack>

        </Group>
      </Box>

      {/* MED-479: Subtask expansion */}
      {hasSubtasks && onToggleExpand && (
        <Collapse in={isExpanded}>
          {/* MED-753: no horizontal indent here so subtask checkboxes line up with the parent's */}
          {isLoadingSubtasks ? (
            <Group py="sm" px="lg" gap="xs">
              <Loader size="xs" />
              <Text size="xs" c="dimmed">Loading subtasks...</Text>
            </Group>
          ) : subtasks && subtasks.length > 0 ? (
            <Stack gap={0}>
              {subtasks.map((subtask) => (
                <CrossCohortSubtaskRow
                  key={subtask.id}
                  subtask={subtask}
                  cohortName={task.cohort_name}
                  cohortId={task.cohort_id}
                  onClick={() => onSubtaskClick?.(subtask)}
                  onToggleComplete={onToggleComplete}
                  isCompact={isCompact}
                />
              ))}
            </Stack>
          ) : null}
        </Collapse>
      )}
    </>
  );
}

// ── Subtask Row for cross-cohort view (maps TaskWithDetails to column layout) ──

function CrossCohortSubtaskRow({
  subtask,
  cohortName,
  cohortId,
  onClick,
  onToggleComplete,
  isCompact = false,
}: {
  subtask: TaskWithDetails;
  cohortName: string;
  cohortId: string;
  onClick: () => void;
  onToggleComplete?: (taskId: string, cohortId: string, currentStatus: TaskStatus) => void;
  isCompact?: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const isComplete = subtask.status === 'complete';
  const statusConfig = TASK_STATUS_CONFIG[subtask.status];
  const overdue = subtask.due_date &&
    isDateOnlyPast(subtask.due_date) &&
    subtask.status !== 'complete' &&
    subtask.status !== 'cancelled';

  const assigneeColWidth = isCompact ? ASSIGNEE_COL_WIDTH_COMPACT : ASSIGNEE_COL_WIDTH_DEFAULT;

  const avatarColor = subtask.assignee_id
    ? getAvatarColor(subtask.assignee_id)
    : getAvatarColor('unassigned');

  return (
    <Box
      className={classes.taskRow}
      style={{
        borderLeft: isHovered ? '3px solid #28463A' : '3px solid transparent',
        backgroundColor: isHovered ? '#F5F7F6' : 'transparent',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <Group
        gap="xl"
        py="sm"
        px="lg"
        style={{
          paddingLeft: isHovered ? 'calc(var(--mantine-spacing-lg) - 3px)' : 'var(--mantine-spacing-lg)',
        }}
      >
        <Box w={34} /> {/* chevron spacer */}
        <Checkbox
          checked={isComplete}
          color="medleyGreen"
          size="sm"
          onChange={(e) => {
            e.stopPropagation();
            onToggleComplete?.(subtask.id, cohortId, subtask.status);
          }}
          onClick={(e) => e.stopPropagation()}
          styles={{ input: { cursor: 'pointer' } }}
        />

        <Group gap={8} wrap="nowrap" align="flex-start" style={{ width: TASK_COL_WIDTH, flexShrink: 0 }}>
          <Text
            size="sm"
            style={{
              textDecoration: isComplete ? 'line-through' : 'none',
              color: isComplete ? 'var(--mantine-color-dimmed)' : '#555555',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {subtask.title}
          </Text>
          {/* MED-770: Copy link — positioned right of title */}
          {isHovered && (
            <Tooltip label="Copy link to task" position="top" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  copyTaskLink(subtask.id, cohortId, 'cross-cohort');
                }}
              >
                <LinkIcon size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        <Text size="xs" c="dimmed" style={{ width: 180, flexShrink: 0 }} truncate>
          {cohortName}
        </Text>

        <Text size="xs" c="dimmed" style={{ width: 170, flexShrink: 0 }} truncate>
          {subtask.phase || '—'}
        </Text>

        <Box style={{ width: 110, flexShrink: 0 }}>
          {subtask.due_date ? (
            <Group gap={4} wrap="nowrap">
              <Calendar size={12} color={overdue ? '#D64545' : '#888888'} />
              <Text size="xs" style={{ color: overdue ? '#D64545' : '#888888' }}>
                {formatDateOnly(subtask.due_date)}
              </Text>
            </Group>
          ) : (
            <Text size="xs" c="dimmed">—</Text>
          )}
        </Box>

        <Box style={{ width: assigneeColWidth, flexShrink: 0 }}>
          {subtask.assignee_name ? (
            <Group gap={6} wrap="nowrap">
              <Tooltip label={subtask.assignee_name} disabled={!isCompact} withArrow>
                <Avatar
                  size="sm"
                  radius="xl"
                  styles={{
                    root: { backgroundColor: avatarColor },
                    placeholder: { backgroundColor: avatarColor, color: '#FFFFFF' },
                  }}
                >
                  {getInitials(subtask.assignee_name)}
                </Avatar>
              </Tooltip>
              {!isCompact && (
                <Text size="xs" c="dimmed" truncate>
                  {subtask.assignee_name.split(' ')[0]}
                </Text>
              )}
            </Group>
          ) : (
            <Text size="xs" c="dimmed">—</Text>
          )}
        </Box>

        <Box style={{ width: 110, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
          <Badge
            size="xs"
            color={statusConfig.color}
            variant={statusConfig.variant}
            styles={{ root: { minWidth: 70, ...(subtask.status === 'not_started' ? { border: 'none', color: '#888888' } : {}) } }}
          >
            {statusConfig.label.toUpperCase()}
          </Badge>
        </Box>

      </Group>
    </Box>
  );
}

// ── Group Section (accordion style matching cohort PhaseAccordion) ────────

function GroupSection({
  groupKey: _groupKey,
  label,
  count,
  tasks,
  expanded,
  onToggle,
  onRowClick,
  hasMore,
  isLoading,
  onShowMore,
  expandedTaskIds,
  loadingTaskIds,
  subtaskCache,
  onToggleExpand,
  onSubtaskClick,
  onToggleComplete,
  isCompact = false,
}: {
  groupKey: string;
  label: string;
  count: number;
  tasks: CrossCohortTaskRow[];
  expanded: boolean;
  onToggle: () => void;
  onRowClick: (task: CrossCohortTaskRow) => void;
  hasMore: boolean;
  isLoading: boolean;
  onShowMore: () => void;
  expandedTaskIds?: Set<string>;
  loadingTaskIds?: Set<string>;
  subtaskCache?: Record<string, TaskWithDetails[]>;
  onToggleExpand?: (taskId: string) => void;
  onSubtaskClick?: (subtask: TaskWithDetails, cohortId: string, cohortName: string) => void;
  onToggleComplete?: (taskId: string, cohortId: string, currentStatus: TaskStatus) => void;
  isCompact?: boolean;
}) {
  // Map status keys to display labels
  const displayLabel = STATUS_GROUP_LABELS[label] || label;
  const remaining = count - tasks.length;

  return (
    <Paper
      withBorder
      radius="md"
      styles={{ root: { boxShadow: 'none' } }}
    >
      {/* Header */}
      <UnstyledButton onClick={onToggle} style={{ width: '100%' }}>
        <Group
          justify="space-between"
          p="md"
          style={{ borderBottom: expanded ? '1px solid var(--mantine-color-gray-2)' : 'none' }}
        >
          <Group gap="md">
            {expanded ? (
              <ChevronDown size={18} color="var(--mantine-color-dimmed)" />
            ) : (
              <ChevronRight size={18} color="var(--mantine-color-dimmed)" />
            )}
            <Text fw={600} size="sm">
              {displayLabel}
            </Text>
            <Badge size="sm" variant="light" color="gray">
              {count}
            </Badge>
          </Group>
        </Group>
      </UnstyledButton>

      {/* Only render rows when expanded (lazy-loaded from API) */}
      {expanded && (
        <>
          {isLoading && tasks.length === 0 ? (
            <Center py="xl">
              <Loader size="sm" />
            </Center>
          ) : (
            <Stack gap={0}>
              {tasks.map((task) => (
                <CrossCohortTaskListRow
                  key={task.id}
                  task={task}
                  onClick={onRowClick}
                  onToggleComplete={onToggleComplete}
                  isExpanded={expandedTaskIds?.has(task.id)}
                  isLoadingSubtasks={loadingTaskIds?.has(task.id)}
                  subtasks={subtaskCache?.[task.id]}
                  onToggleExpand={onToggleExpand}
                  onSubtaskClick={(subtask) => onSubtaskClick?.(subtask, task.cohort_id, task.cohort_name)}
                  isCompact={isCompact}
                />
              ))}
            </Stack>
          )}
          {hasMore && remaining > 0 && (
            <Box py="sm" px="lg" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
              <Button
                variant="subtle"
                color="gray"
                size="sm"
                fullWidth
                onClick={onShowMore}
                loading={isLoading && tasks.length > 0}
              >
                Show more ({remaining} remaining)
              </Button>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}

export default TasksPage;
